import type { SupabaseClient } from "@supabase/supabase-js";

// Identity Wave B lifecycle adapters (migration 20260802150000). Thin wire ONLY: every
// mutation goes through the six hosted RPCs; there is no client-side status write and no
// table DML fallback anywhere in this module. Server gates are authoritative
// (member.manage for member lifecycle, admin role for the account profile, admin or
// supervisor role for device trust); the UI display gates are a courtesy on top.
//
// Error doctrine matches members.ts: contract-stable, value-free sentences from the
// migration render VERBATIM; anything else fails closed to a safe generic. Suspend and
// remove return the server's side-effect counts so surfaces can show the honest
// cancel-trail copy (future assignments released for reassignment, push tokens retired).

export const SAFE_MEMBER_LIFECYCLE_FAILURE =
  "Couldn't complete that member change - please try again.";
export const SAFE_ACCOUNT_PROFILE_FAILURE =
  "Couldn't save the account name - please try again.";
export const SAFE_DEVICE_TRUST_FAILURE =
  "Couldn't complete that device trust change - please try again.";

// Contract-stable sentences from 20260802150000 (member lifecycle definers).
const KNOWN_MEMBER_LIFECYCLE_FAILURES: ReadonlySet<string> = new Set([
  "Member management permission is required.",
  "Member lifecycle caller mismatch.",
  "Lifecycle reason must be 500 characters or fewer.",
  "Membership was not found for this account.",
  "You cannot suspend your own membership.",
  "You cannot remove your own membership.",
  "Only an active membership can be suspended.",
  "Only a suspended membership can be restored.",
  "Only an active or suspended membership can be removed.",
  "At least one active admin is required for this account.",
]);

const KNOWN_ACCOUNT_PROFILE_FAILURES: ReadonlySet<string> = new Set([
  "Account administrator role is required to update the account profile.",
  "Account profile caller mismatch.",
  "Account name is required.",
  "Account name must be 120 characters or fewer.",
  "Account was not found.",
]);

const KNOWN_DEVICE_TRUST_FAILURES: ReadonlySet<string> = new Set([
  "Only account admins and supervisors can manage device trust.",
  "Device trust caller mismatch.",
  "A reason is required to revoke a device.",
  "Device revoke reason must be 500 characters or fewer.",
  "Device was not found for this account.",
  "Device is already revoked.",
  "Device is not revoked.",
]);

function mapKnownFailure(message: string, known: ReadonlySet<string>, safe: string): string {
  return known.has(message) ? message : safe;
}

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function firstRow(data: unknown): Record<string, unknown> | undefined {
  return Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
}

/**
 * Counts returned by suspend and remove so the UI can state the side effects honestly:
 * released future assignments stay on the schedule as cancelled rows that keep their
 * prior assignee, ready to reassign; nothing is deleted.
 */
export interface MemberLifecycleOutcomeCounts {
  readonly pushTokensRetired: number;
  readonly scheduleAssignmentsReleased: number;
}

export type MemberLifecycleOutcome =
  | ({ ok: true } & MemberLifecycleOutcomeCounts)
  | { ok: false; reason: string };

async function runMemberLifecycle(
  client: SupabaseClient,
  fn: "suspend_account_member" | "remove_account_member",
  params: Record<string, unknown>,
): Promise<MemberLifecycleOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: mapKnownFailure(message, KNOWN_MEMBER_LIFECYCLE_FAILURES, SAFE_MEMBER_LIFECYCLE_FAILURE),
      };
    }
    const row = firstRow(data);
    return {
      ok: true,
      pushTokensRetired: asCount(row?.push_tokens_retired),
      scheduleAssignmentsReleased: asCount(row?.schedule_assignments_released),
    };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_LIFECYCLE_FAILURE };
  }
}

export async function suspendAccountMember(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  reason: string | null = null,
): Promise<MemberLifecycleOutcome> {
  return runMemberLifecycle(client, "suspend_account_member", {
    p_account_id: accountId,
    p_membership_id: membershipId,
    p_reason: reason?.trim() ? reason.trim() : null,
  });
}

export async function removeAccountMember(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  reason: string | null = null,
): Promise<MemberLifecycleOutcome> {
  return runMemberLifecycle(client, "remove_account_member", {
    p_account_id: accountId,
    p_membership_id: membershipId,
    p_reason: reason?.trim() ? reason.trim() : null,
  });
}

export type SimpleLifecycleOutcome = { ok: true } | { ok: false; reason: string };

export async function restoreAccountMember(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
): Promise<SimpleLifecycleOutcome> {
  try {
    const { error } = await client.rpc("restore_account_member", {
      p_account_id: accountId,
      p_membership_id: membershipId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: mapKnownFailure(message, KNOWN_MEMBER_LIFECYCLE_FAILURES, SAFE_MEMBER_LIFECYCLE_FAILURE),
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_LIFECYCLE_FAILURE };
  }
}

export type AccountProfileOutcome =
  | { ok: true; name: string }
  | { ok: false; reason: string };

/** Name only by contract; the RPC has no slug parameter and never will through this wire. */
export async function updateAccountProfile(
  client: SupabaseClient,
  accountId: string,
  name: string,
): Promise<AccountProfileOutcome> {
  try {
    const { data, error } = await client.rpc("update_account_profile", {
      p_account_id: accountId,
      p_name: name.trim(),
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: mapKnownFailure(message, KNOWN_ACCOUNT_PROFILE_FAILURES, SAFE_ACCOUNT_PROFILE_FAILURE),
      };
    }
    const row = firstRow(data);
    const savedName = typeof row?.name === "string" && row.name.length > 0 ? row.name : name.trim();
    return { ok: true, name: savedName };
  } catch {
    return { ok: false, reason: SAFE_ACCOUNT_PROFILE_FAILURE };
  }
}

export type DeviceTrustOutcome =
  | { ok: true; pushTokensRetired: number }
  | { ok: false; reason: string };

/** Reason is required by the server; the adapter refuses an empty one before the network. */
export async function revokeAccountDevice(
  client: SupabaseClient,
  accountId: string,
  deviceRowId: string,
  reason: string,
): Promise<DeviceTrustOutcome> {
  const trimmedReason = reason.trim();
  if (trimmedReason.length === 0) {
    return { ok: false, reason: "A reason is required to revoke a device." };
  }
  try {
    const { data, error } = await client.rpc("revoke_account_device", {
      p_account_id: accountId,
      p_device_id: deviceRowId,
      p_reason: trimmedReason,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: mapKnownFailure(message, KNOWN_DEVICE_TRUST_FAILURES, SAFE_DEVICE_TRUST_FAILURE),
      };
    }
    const row = firstRow(data);
    return { ok: true, pushTokensRetired: asCount(row?.push_tokens_retired) };
  } catch {
    return { ok: false, reason: SAFE_DEVICE_TRUST_FAILURE };
  }
}

export async function restoreAccountDevice(
  client: SupabaseClient,
  accountId: string,
  deviceRowId: string,
): Promise<SimpleLifecycleOutcome> {
  try {
    const { error } = await client.rpc("restore_account_device", {
      p_account_id: accountId,
      p_device_id: deviceRowId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: mapKnownFailure(message, KNOWN_DEVICE_TRUST_FAILURES, SAFE_DEVICE_TRUST_FAILURE),
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_DEVICE_TRUST_FAILURE };
  }
}
